import { useMemo, useState } from 'react'
import { useDebounce } from '@node-space/hooks'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'

export type FilterShape = Record<string, string[]>

export const useSearchAndFilter = <FilterType extends FilterShape>(
  initialSearch = '',
  initialFilters?: FilterType
) => {
  const [searchValue, setSearchValue] = useState(initialSearch)
  const [appliedFilters, setAppliedFilters] = useState<FilterType>(
    (initialFilters ?? {}) as FilterType
  )

  const debouncedSearchQuery = useDebounce(searchValue, SEARCH_INPUT_DEBOUNCE_TIME)

  const appliedSearch = useMemo(
    () => (searchValue ? debouncedSearchQuery : searchValue),
    [searchValue, debouncedSearchQuery]
  )

  const hasSearchAndFilter = useMemo(
    () => !!appliedSearch || !!Object.values(appliedFilters ?? {})?.length,
    [appliedSearch, appliedFilters]
  )

  const getUpdatedFilters = (currentFilters: string[], newFilter: string) => {
    if (currentFilters?.includes(newFilter)) {
      return currentFilters?.filter(filter => filter !== newFilter)
    }
    return [...(currentFilters || []), newFilter]
  }

  const onFilterChange = (filterKey: keyof FilterType, filterValue: string) => {
    setAppliedFilters(current => {
      const updatedFilters = getUpdatedFilters(current?.[filterKey], filterValue)
      if (updatedFilters?.length) {
        return { ...current, [filterKey]: updatedFilters }
      }
      if (current?.[filterKey]) {
        const { [filterKey]: _, ...remainingFilters } = current || {}
        return (remainingFilters || {}) as FilterType
      }
      return current
    })
  }

  const onResetAll = () => {
    setAppliedFilters({} as FilterType)
    setSearchValue('')
  }

  return {
    searchValue,
    appliedSearch,
    appliedFilters,
    hasSearchAndFilter,
    setSearchValue,
    setAppliedFilters,
    onFilterChange,
    onResetAll,
  }
}
